import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { EdgeFunctionError } from "@/lib/edge-functions";
import {
  useGuaranteeReviewHistory,
  useGuaranteeReviewQueue,
  useReviewRiderGuarantee,
  useRiderSessionOutcomes,
  type GuaranteeQueueRow,
  type RiderSessionOutcomeRow,
} from "../rider-sessions.hooks";
import { GuaranteeReviewDialog } from "./GuaranteeReviewDialog";
import {
  CheckCircleIcon,
  ClockCountdownIcon,
  FlagIcon,
  SignOutIcon,
  UserMinusIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { StateSummary } from "./StateSummary";

/** The five attendance results (CORE-04). No grace period on either side. */
const OUTCOME_META: Record<string, { label: string; variant: BadgeVariant }> = {
  completed: { label: "Completed", variant: "success" },
  late_start: { label: "Late Start", variant: "warning" },
  early_end: { label: "Early End", variant: "warning" },
  incomplete: { label: "Incomplete", variant: "destructive" },
  no_show: { label: "No-Show", variant: "destructive" },
};

const peso = (value: unknown) => `₱${Number(value ?? 0).toFixed(2)}`;

interface OutcomesPanelProps {
  hubId: string | null;
  /**
   * Rider pay — settled earnings and guarantee decisions. hub_manager holds no
   * grant on either (0064, 0067: "this is pay"), so for that role the columns
   * and the guarantee card are absent and the reads are never made. Before this,
   * the reads were made anyway: the outcomes table failed outright and the
   * guarantee card swallowed its error and said "Nothing waiting".
   */
  canSeePay: boolean;
  /**
   * RIDER-028's authority is undefined, so the review control is superadmin-only
   * (never-invent-policy.md §2). The other pay-reading roles see the queue and
   * the decisions — withholding the record too would hide that an authority
   * exists at all.
   */
  canReview: boolean;
}

export function OutcomesPanel({ hubId, canSeePay, canReview }: OutcomesPanelProps) {
  const { data: outcomes, isLoading, isError } = useRiderSessionOutcomes(hubId, 30, canSeePay);
  const {
    data: queue,
    isLoading: queueLoading,
    isError: queueError,
  } = useGuaranteeReviewQueue(canSeePay);
  const { data: reviews } = useGuaranteeReviewHistory(canSeePay);
  const review = useReviewRiderGuarantee();
  const [target, setTarget] = useState<GuaranteeQueueRow | null>(null);

  const closeReview = () => {
    setTarget(null);
    // A failed review must not leave its error on the card after the dialog closes.
    review.reset();
  };

  const rows = outcomes ?? [];
  const countOf = (outcome: string) => rows.filter((r) => r.outcome === outcome).length;
  const columns: ColumnDef<RiderSessionOutcomeRow, unknown>[] = [
    {
      id: "rider",
      accessorFn: (row) => row.staff?.full_name ?? "",
      header: "Rider",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.staff?.full_name ?? "—"}
          subtitle={`${row.original.code} · ${row.original.service_date}`}
          fallback={(row.original.staff?.full_name ?? "RI").slice(0, 2).toUpperCase()}
        />
      ),
    },
    { id: "date", accessorFn: (row) => row.service_date, header: "Date" },
    { id: "session", accessorFn: (row) => row.code, header: "Session" },
    {
      id: "outcome",
      accessorFn: (row) => row.outcome ?? "",
      header: "Outcome",
      cell: ({ row }) => {
        const meta = row.original.outcome ? OUTCOME_META[row.original.outcome] : undefined;
        return meta ? <Badge variant={meta.variant}>{meta.label}</Badge> : "—";
      },
    },
    {
      id: "extension",
      accessorFn: (row) => row.actual_extension_minutes ?? 0,
      header: "Extension",
      cell: ({ row }) =>
        row.original.actual_extension_minutes ? `${row.original.actual_extension_minutes}m` : "—",
    },
    ...(canSeePay
      ? [
          {
            id: "dispatches",
            accessorFn: (row: RiderSessionOutcomeRow) =>
              row.rider_session_earnings?.dispatch_count ?? 0,
            header: "Dispatches",
          },
          {
            id: "km",
            accessorFn: (row: RiderSessionOutcomeRow) => row.rider_session_earnings?.total_km ?? 0,
            header: "Km",
          },
          {
            id: "earned",
            accessorFn: (row: RiderSessionOutcomeRow) =>
              Number(row.rider_session_earnings?.total_php ?? 0),
            header: "Earned",
            cell: ({ row }: { row: { original: RiderSessionOutcomeRow } }) =>
              row.original.rider_session_earnings
                ? peso(row.original.rider_session_earnings.total_php)
                : "Not settled",
          },
        ]
      : []),
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.code}
          actions={[
            {
              label: "Copy Session ID",
              onSelect: () => void navigator.clipboard.writeText(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <StateSummary
        isLoading={isLoading}
        items={[
          { label: "Finished", value: rows.length, icon: FlagIcon },
          {
            label: "Completed",
            value: countOf("completed"),
            icon: CheckCircleIcon,
            tone: "text-success",
          },
          {
            label: "Late Start",
            value: countOf("late_start"),
            icon: ClockCountdownIcon,
            tone: "text-warning",
          },
          {
            label: "Early End",
            value: countOf("early_end"),
            icon: SignOutIcon,
            tone: "text-warning",
          },
          {
            label: "Incomplete",
            value: countOf("incomplete"),
            icon: WarningIcon,
            tone: "text-destructive",
          },
          {
            label: "No-Show",
            value: countOf("no_show"),
            icon: UserMinusIcon,
            tone: "text-destructive",
          },
        ]}
      />

      {/* The review queue first — it is the only thing on this page that waits
          on a person. Absent for roles that may not read rider pay. */}
      {canSeePay ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Guarantee review</CardTitle>
            <span className="text-xs text-muted-foreground">RIDER-028 · superadmin only</span>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Sessions an emergency closed short. The guarantee is forfeit on these outcomes, and
              RIDER-028 allows an Operations review — which role holds that authority is not defined
              in the spec, so it is withheld to superadmin and every decision is audited.
            </p>

            {queueError ? (
              <p className="text-sm text-destructive">
                Couldn&rsquo;t load the review queue — check your connection and try again.
              </p>
            ) : queueLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : !queue || queue.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nothing waiting</EmptyTitle>
                  <EmptyDescription>
                    No Session is flagged for a guarantee decision right now.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <ul className="divide-y divide-border/60">
                {queue.map((row) => (
                  <li
                    key={row.rider_session_id}
                    className="flex flex-wrap items-center justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {row.rider_name}
                        <span className="ml-2 text-xs font-normal capitalize text-muted-foreground">
                          {row.rider_rank}
                        </span>
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        Session {row.code} · {row.service_date} · settled {peso(row.settled_total)}
                        {row.hub_name ? ` · ${row.hub_name}` : ""}
                      </p>
                    </div>
                    {canReview ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          review.reset();
                          setTarget(row);
                        }}
                      >
                        Review
                      </Button>
                    ) : (
                      <Badge variant="muted">Awaiting superadmin</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {reviews && reviews.length > 0 ? (
              <div className="border-t border-border/60 pt-3">
                <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
                  Recent decisions
                </h3>
                <ul className="space-y-1.5">
                  {reviews.map((decision) => (
                    <li
                      key={decision.rider_session_id}
                      className="flex items-center justify-between gap-3 text-xs"
                    >
                      <span className="min-w-0 truncate text-muted-foreground">
                        {decision.staff?.full_name ?? "Rider"}
                        {decision.rider_sessions
                          ? ` · Session ${decision.rider_sessions.code} · ${decision.rider_sessions.service_date}`
                          : ""}{" "}
                        — {decision.reason}
                      </span>
                      <Badge variant={decision.decision === "approved" ? "success" : "muted"}>
                        {decision.decision === "approved"
                          ? `Approved ${peso(decision.amount_php)}`
                          : "Declined"}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Finished Sessions — last 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <p className="text-sm text-destructive">
              Couldn&rsquo;t load outcomes — check your connection and try again.
            </p>
          ) : (
            <AdminDataTable
              presentation="embedded"
              data={rows}
              columns={columns}
              getRowId={(row) => row.id}
              searchText={(row) =>
                [row.staff?.full_name, row.code, row.service_date, row.outcome].join(" ")
              }
              searchPlaceholder="Search rider, Session, date, or outcome…"
              facets={[
                {
                  id: "outcome",
                  label: "Outcome",
                  options: Object.entries(OUTCOME_META).map(([value, meta]) => ({
                    value,
                    label: meta.label,
                  })),
                  getValue: (row) => row.outcome ?? "",
                },
              ]}
              initialSorting={[{ id: "date", desc: true }]}
              loading={isLoading}
              emptyTitle="No finished Sessions"
              emptyDescription="Outcomes appear once Sessions close."
              exportFilename="rider-session-outcomes"
              exportColumns={[
                { header: "Date", value: (row) => row.service_date },
                { header: "Rider", value: (row) => row.staff?.full_name },
                { header: "Session", value: (row) => row.code },
                { header: "Outcome", value: (row) => row.outcome },
                { header: "Extension minutes", value: (row) => row.actual_extension_minutes },
                ...(canSeePay
                  ? [
                      {
                        header: "Dispatches",
                        value: (row: RiderSessionOutcomeRow) =>
                          row.rider_session_earnings?.dispatch_count,
                      },
                      {
                        header: "Km",
                        value: (row: RiderSessionOutcomeRow) =>
                          row.rider_session_earnings?.total_km,
                      },
                      {
                        header: "Earned",
                        value: (row: RiderSessionOutcomeRow) =>
                          row.rider_session_earnings?.total_php,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </CardContent>
      </Card>

      <GuaranteeReviewDialog
        session={target}
        isPending={review.isPending}
        errorMessage={
          review.isError
            ? review.error instanceof EdgeFunctionError
              ? review.error.message
              : (review.error as Error).message
            : null
        }
        onClose={closeReview}
        onSubmit={(input) => {
          if (!target) return;
          review.mutate(
            { sessionId: target.rider_session_id as string, ...input },
            { onSuccess: closeReview },
          );
        }}
      />
    </div>
  );
}
